import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { confirmRefrenceCondition } from "../../network/cipAPI";

import createNotification, {
  SUCCESS_NOTIFICATION,
  ERROR_NOTIFICATION,
} from "../../components/Notifications";
import CircularLoader from "../../common/styles/components/circularLoader";
import { TextFieldValidator } from "../../components/InputBox/TextFieldValidator";

import { UpdateSetWithKey, ReferenceUnitConverter } from "../../utils/Utils";
import { ALLOWED_MIN_CONVERSION_UNITS } from "../../constants/global.constants";
import { REFERENCE_CONDITION_OPTIONS } from "./columns.config";

import { WPRangedInput } from "../../packages/WPInput/WPRangedInput";
import WPTertiaryButton from "../../packages/WPTertiaryButton";
import { SaveIcon } from "../../packages/SVGs";

import "./index.scss";

const ReferenceCondition = ({
  fileId,
  mappedReference,
  tempReferenceValues,
  isEdit,
  setisReferenceEnabled,
  selectedColumnRadioColumn,
  selectedTrain,
  setIsProceedDisabled,
}) => {
  const { t } = useTranslation();

  const referenceColumns = useMemo(
    () => REFERENCE_CONDITION_OPTIONS(t, selectedColumnRadioColumn),
    [t, selectedColumnRadioColumn]
  );

  const { flow, pressure, feed_quality, permeate_quality, temperature } =
    useSelector((state) => state);

  const unitSettings = useMemo(
    () => ({
      flow,
      pressure,
      feed_quality,
      permeate_quality,
      temperature: `\u00B0${temperature}`,
    }),
    [flow, pressure, feed_quality, permeate_quality, temperature]
  );

  const [selectedReference, setSelectedReference] = useState([]);
  const [changedSelectedReference, setChangedSelectedReference] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isAnimatingTempRef, setIsAnimatingTempRef] = useState(false);
  const [errors, setErrors] = useState({
    feed_flowrate: null,
    concentrate_flowrate: null,
    permeate_flowrate: null,
    feed_pressure: null,
    permeate_pressure: null,
    feed_temp: null,
    permeate_quality: null,
    feed_quality: null,
    pressure_drop: null,
    concentrate_pressure: null,
  });

  const callRangeValidation = (referenceValues, isChanged = true) => {
    Object.keys(referenceValues).forEach((key) => {
      const fieldConfig = referenceColumns.find((field) => field.key === key);
      if (fieldConfig) {
        const ranges = fieldConfig.ranges?.hard;
        const convertedRanges = getConvertedRanges(ranges, fieldConfig.unit);
        onInputValChange(
          referenceValues[key],
          key,
          convertedRanges.hard,
          isChanged
        );
      }
    });
  };

  useEffect(() => {
    if (tempReferenceValues) {
      setIsAnimatingTempRef(true);
      callRangeValidation(tempReferenceValues);
      // Reset animation after 2 seconds
      setTimeout(() => {
        setIsAnimatingTempRef(false);
      }, 2000);
    }
  }, [tempReferenceValues]);

  useEffect(() => {
    if (mappedReference) {
      const formattedReference = {};
      for (const key in mappedReference) {
        const val = mappedReference[key];
        if (typeof val === "number") {
          formattedReference[key] =
            val === 0 ? val.toString() : Number(val.toFixed(2));
        } else {
          formattedReference[key] = val;
        }
      }
      callRangeValidation(formattedReference, false);
      if (isEdit) {
        setisReferenceEnabled(false);
      }
    } else {
      setSelectedReference({});
    }
  }, [mappedReference, selectedColumnRadioColumn]);

  useEffect(() => {
    if (!fileId) {
      setSelectedReference([]);
      setChangedSelectedReference([]);
    }
  }, [fileId]);

  const getConvertedRanges = (ranges, unit) => {
    let { min, max } = ranges;
    const isMinConversionAllowed = ALLOWED_MIN_CONVERSION_UNITS.includes(unit);
    const convertedMin = isMinConversionAllowed
      ? ReferenceUnitConverter(min, unitSettings[unit])
      : min;
    const convertedMax = ReferenceUnitConverter(max, unitSettings[unit]);

    let covertedRanges = { hard: { min: convertedMin, max: convertedMax } };
    return covertedRanges;
  };

  const onInputValChange = (value, key, range, isChanged = true) => {
    const { min, max } = range;

    if (value !== "" && (parseFloat(value) < min || parseFloat(value) > max)) {
      setErrors((prev) => ({ ...prev, [key]: true }));
    } else {
      setErrors((prev) => ({ ...prev, [key]: null }));
    }
    setisReferenceEnabled(false);
    setSelectedReference((prev) => ({ ...prev, [key]: value }));
    isChanged &&
      setChangedSelectedReference((prevSet) =>
        UpdateSetWithKey(prevSet, key, value)
      );
  };

  const getConfirmButtonState = () => {
    const {
      feed_flowrate,
      concentrate_flowrate,
      permeate_flowrate,
      feed_pressure,
      permeate_pressure,
      feed_temp,
      permeate_quality,
      feed_quality,
      concentrate_pressure,
      pressure_drop,
    } = selectedReference;

    const hasAllInputs =
      (feed_flowrate || concentrate_flowrate) &&
      permeate_flowrate &&
      feed_pressure &&
      permeate_pressure &&
      feed_temp &&
      permeate_quality &&
      feed_quality &&
      (concentrate_pressure || pressure_drop);

    if (
      (!isEdit && hasAllInputs) ||
      (isEdit &&
        hasAllInputs &&
        Object.keys(changedSelectedReference).length > 0 &&
        Object.values(errors).every((error) => error === null))
    ) {
      return false;
    } else return true;
  };

  const confirmRefrenceConditions = async () => {
    setIsSaving(true);
    try {
      let payload = null;
      if (mappedReference) payload = changedSelectedReference;
      else payload = selectedReference;
      const response = await confirmRefrenceCondition(
        fileId,
        payload,
        selectedTrain
      );

      if (response && response.detail === undefined) {
        setChangedSelectedReference([]);
        setIsProceedDisabled(!response.analysis);
        createNotification(
          SUCCESS_NOTIFICATION,
          t("referenceConditions.referenceAdded"),
          ""
        );
      } else {
        const errorMsg = response.detail?.error || response.detail;
        createNotification(
          ERROR_NOTIFICATION,
          errorMsg || t("ValueStrings.somethingWentWrong"),
          ""
        );
      }
    } catch (err) {
      console.log("confirmRefrenceCondition:", err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="ro-card-wrapper margin-top-20 position-relative">
      {isSaving && (
        <div className="circular-loader-overlay">
          <CircularLoader />
        </div>
      )}
      <div className="teal-heading header-border-bottom flex-between">
        {t("referenceConditions.heading")}

        <WPTertiaryButton
          variant="outlined"
          label={t("ValueStrings.confirm")}
          icon={<SaveIcon width="16" height="16" />}
          iconPlacement="before"
          size="medium"
          onClick={confirmRefrenceConditions}
          disabled={getConfirmButtonState()}
          className={`${
            isAnimatingTempRef && !getConfirmButtonState()
              ? "temp-reference-animating"
              : ""
          }`}
        />
      </div>
      <div className="ro-component-description">
        {t("referenceConditions.description")}
      </div>
      <div className="reference-condition-container">
        {referenceColumns.map((field) => {
          const convertedRanges = getConvertedRanges(
            field.ranges?.hard,
            field.unit
          );

          return (
            <div className="reference-item">
              <WPRangedInput
                key={field.key}
                value={selectedReference[field.key] || ""}
                name={field.key}
                label={field.name}
                suffix={unitSettings[field.unit]}
                ranges={convertedRanges}
                error={errors[field.key]}
                onChange={(e) =>
                  onInputValChange(
                    e.target.value,
                    field.key,
                    convertedRanges.hard
                  )
                }
                validator={TextFieldValidator.NUMBER_TWO_DECIMAL}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};
export default ReferenceCondition;
